import {Ref} from "vue";
import {Store} from "vuex";
import axios from "axios";
import {helper} from "@/service/CommonHelper";
import ExportTaskResponseDTO from "@/models/ExportTaskResponseDTO";
import ConfirmModal, {ConfirmModalProps} from "@/components/ConfirmModal.vue";

export type ExportTaskCreateRequestDTO = Record<string, any>;

function activeTaskLabel(task: ExportTaskResponseDTO): string {
    return helper.trans('export_tasks.confirm_replace', {
        sysname: helper.trans(`export_tasks.sysname.${task.sysname}`, {}, task.sysname),
        progress: helper.trans('export_tasks.progress', {
            sheet: task.progress?.sheet ?? 0,
            of: task.progress?.of ?? 0,
            rows: task.progress?.rows ?? 0,
        }),
    });
}

/**
 * Creates an export task; on 409 (another task already active) asks via ConfirmModal
 * whether to cancel it and retry. Rethrows any other error for the caller to handle.
 */
export async function createExportTask(
    store: Store<any>,
    confirmModal: Ref<InstanceType<typeof ConfirmModal> | null>,
    sysname: string,
    requestDTO: ExportTaskCreateRequestDTO,
): Promise<ExportTaskResponseDTO> {
    try {
        return await store.dispatch('exportTasks/create', {sysname, requestDTO});
    } catch (e) {
        if (axios.isAxiosError(e) && e.response?.status === 409) {
            const activeTask = e.response.data as ExportTaskResponseDTO;
            return new Promise((resolve, reject) => {
                confirmModal.value?.show({
                    title: activeTaskLabel(activeTask),
                    okTitle: null,
                    cancelTitle: null,
                    onOkClick: async () => {
                        await store.dispatch('exportTasks/cancel', activeTask.id);
                        try {
                            resolve(await createExportTask(store, confirmModal, sysname, requestDTO));
                        } catch (retryError) {
                            reject(retryError);
                        }
                    },
                } as ConfirmModalProps);
            });
        }

        throw e;
    }
}
